import { useEffect, useState } from "react";
import { api, ApiError } from "../api/http.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { toast } from "../ui/Toast.js";

export interface Invite {
  code: string;
  createdAt: number;
  usedAt: number | null;
  usedBy: string | null;
}

interface InviteList {
  invites: Invite[];
  open: number;
  max: number;
}

/** Own invite codes (D78): every account may mint them, one code admits one player, inviting pays nothing. */
export function InvitePanel() {
  const t = useT();
  const [list, setList] = useState<InviteList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => setList(await api<InviteList>("GET", "/api/auth/invites"));
  useEffect(() => {
    void load().catch(() => undefined);
  }, []);

  const mint = async () => {
    setBusy(true);
    setError(null);
    try {
      await api<{ invite: Invite }>("POST", "/api/auth/invites");
      await load();
    } catch (e) {
      setError(e instanceof ApiError && e.code === "INVITE_LIMIT" ? t("settings.invites.limit", { max: list?.max ?? 5 }) : t("settings.invites.failed"));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("settings.invites.copied"));
    } catch {
      toast.error(t("settings.invites.copyFailed"));
    }
  };

  return (
    <Panel title={t("settings.invites")}>
      <p className="hint" style={{ marginTop: 0 }}>{t("settings.invites.hint")}</p>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span className="mono dim" data-testid="invite-open">{list ? t("settings.invites.open", { open: list.open, max: list.max }) : t("common.loading")}</span>
        <Button variant="primary" onClick={() => void mint()} loading={busy} data-testid="invite-create">
          {t("settings.invites.create")}
        </Button>
      </div>
      {error ? <p className="error" role="alert">{error}</p> : null}
      {list && list.invites.length === 0 ? <p className="dim">{t("settings.invites.none")}</p> : null}
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
        {list?.invites.map((invite) => (
          <li key={invite.code} className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <code className="mono" style={{ userSelect: "all", letterSpacing: 1 }} data-testid="invite-code">{invite.code}</code>
            <span className="dim" style={{ flex: 1, minWidth: 0 }}>
              {invite.usedBy ? t("settings.invites.used", { name: invite.usedBy }) : t("settings.invites.free")}
            </span>
            {invite.usedBy ? null : (
              <Button onClick={() => void copy(invite.code)} data-testid="invite-copy">
                {t("settings.invites.copy")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
